import { useContext, useEffect, useState } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router-dom'

import { AuthContext } from '../context/AuthContext'
import { getSessionById } from '../service/sessionService'
import { getTelemetryEventsBySessionAndUser } from '../component/workspace/service/telemetryService'
import { latestSubmissionFromEvents } from '../component/workspaceReplay2/utils/latestSubmissionFromEvents'
import { isGuiSession } from '../component/workspace/config/guiSessionConfig'
import Navbar from '../component/workspace/components/Navbar'
import ReplayCodeView from '../component/workspaceReplay2/components/ReplayCodeView'
import ReplayTestCases from '../component/workspaceReplay2/components/ReplayTestCases'
import ReplayTerminal from '../component/workspaceReplay2/components/ReplayTerminal'
import ReplayTaskDescription from '../component/workspaceReplay2/components/ReplayTaskDescription'

// Read-only "what did I submit" view. Deliberately not CodeContext/CodeEditor.jsx (those
// only persist to localStorage, fire telemetry on every keystroke, and have no readOnly
// mode) and not the full workspaceReplay2 scrubbable-timeline tool (built for researchers
// stepping through a whole session, not a student checking their final answer) - see the
// plan doc for why. Source of truth is this student's own telemetry event stream, reduced
// to the final code/test-result state.
const StudentRevisitPage = () => {
  const { sessionId } = useParams()
  const { currentUser, currentUserLoading } = useContext(AuthContext)
  const navigate = useNavigate()
  const [session, setSession] = useState(null)
  const [submission, setSubmission] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!currentUser?.id || !sessionId) return
    Promise.all([
      getSessionById(sessionId),
      getTelemetryEventsBySessionAndUser(sessionId, currentUser.id),
    ]).then(([sessionData, events]) => {
      setSession(sessionData)
      setSubmission(latestSubmissionFromEvents(events))
    }).finally(() => setLoading(false))
  }, [currentUser?.id, sessionId])

  if (currentUserLoading) return <div className="home"><Navbar sessionId={null} /></div>
  if (!currentUser) return <Navigate to="/login" replace />
  if (currentUser.role !== 4) return <Navigate to="/" replace />

  const isWebDev = session && isGuiSession(session)

  return (
    <div className="home">
      <Navbar sessionId={null} />
      <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 20px 48px' }}>
        <button
          onClick={() => navigate('/my-exercises')}
          style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, marginBottom: '16px', fontSize: '0.85rem' }}
        >
          ← Back to My Exercises
        </button>

        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px',
          padding: '8px 14px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #fde68a',
          fontSize: '0.82rem', color: '#92400e', width: 'fit-content',
        }}>
          Read-only — this is what you submitted. You can't edit it here.
        </div>

        {loading ? (
          <div style={{ color: '#6b7280' }}>Loading...</div>
        ) : (
          <>
            <ReplayTaskDescription task={session?.task} />

            <div style={{ height: isWebDev ? 'auto' : '360px', minHeight: isWebDev ? '480px' : undefined, border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', marginTop: '16px', display: 'flex' }}>
              <ReplayCodeView code={submission?.code || ''} isGuiSession={isWebDev} />
            </div>

            <div style={{ marginTop: '16px' }}>
              <ReplayTestCases testResults={submission?.testResults || []} />
            </div>

            {submission?.terminalOutput?.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <ReplayTerminal height={140} output={submission.terminalOutput} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default StudentRevisitPage
